// Lightweight per-route document meta: <title>, description, canonical, OG.
// SPA-only — sufficient for Google + the basics. SSR is a separate, larger pass.

import { useEffect } from "react";
import { BRAND_ASSETS_ABSOLUTE } from "@/components/brand/assets";

export interface PageMeta {
  title: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
}

const DEFAULT_DESCRIPTION =
  "Decyzon — the decision layer for Polymarket. Find mispriced YES/NO and multi-outcome markets, ranked by what's actionable, what to track, and what to skip.";

const DEFAULT_OG = BRAND_ASSETS_ABSOLUTE.ogImage;

function setMeta(name: string, content: string, attr: "name" | "property" = "name") {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function usePageMeta(meta: PageMeta) {
  useEffect(() => {
    document.title = meta.title;
    const description = meta.description ?? DEFAULT_DESCRIPTION;
    const canonical = meta.canonical ?? `https://www.decyzon.com${window.location.pathname}`;
    const ogImage = meta.ogImage ?? DEFAULT_OG;
    setMeta("description", description);
    setLink("canonical", canonical);
    setMeta("og:title", meta.title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:image", ogImage, "property");
    setMeta("twitter:title", meta.title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);
  }, [meta.title, meta.description, meta.canonical, meta.ogImage]);
}
